import { useState, useEffect } from 'react';
import { Clock, Newspaper, AlertTriangle, ChevronDown } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from './ui/tooltip';
import API from '../utils/api';

// Impact color mapping
const IMPACT_COLORS = {
  high: { text: 'text-red-400', dot: 'bg-red-500' },
  medium: { text: 'text-orange-400', dot: 'bg-orange-500' },
  low: { text: 'text-yellow-400', dot: 'bg-yellow-500' }
};

// Country flag emojis
const COUNTRY_FLAGS = {
  USD: '🇺🇸',
  EUR: '🇪🇺',
  GBP: '🇬🇧',
  JPY: '🇯🇵',
  CHF: '🇨🇭',
  AUD: '🇦🇺',
  CAD: '🇨🇦',
  NZD: '🇳🇿',
  CNY: '🇨🇳',
  ALL: '🌍'
};

// Trading Sessions (in hours, CET timezone)
const SESSIONS = [
  { name: 'Asia', start: 1, end: 10, color: 'bg-purple-500' },
  { name: 'London', start: 8, end: 17, color: 'bg-blue-500' },
  { name: 'NY', start: 14, end: 23, color: 'bg-emerald-500' }
];

const TradingDayWidget = () => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [economicEvents, setEconomicEvents] = useState([]);
  const [showEvents, setShowEvents] = useState(false);
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState(false);

  // Update time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Calculate live countdown for an event
  const getCountdown = (eventDate) => {
    const now = new Date();
    const eventTime = new Date(eventDate);
    const diffMs = eventTime - now;
    const diffSeconds = diffMs / 1000;
    
    if (diffSeconds < -3600) {
      return { text: 'Vorbei', isPast: true, diffSeconds };
    } else if (diffSeconds < 0) {
      return { text: 'Läuft', isPast: false, diffSeconds };
    } else if (diffSeconds < 3600) {
      return { text: `${Math.floor(diffSeconds / 60)}m`, isPast: false, diffSeconds };
    } else if (diffSeconds < 86400) {
      const hours = Math.floor(diffSeconds / 3600);
      const minutes = Math.floor((diffSeconds % 3600) / 60);
      return { text: `${hours}h ${minutes}m`, isPast: false, diffSeconds };
    } else {
      const days = Math.floor(diffSeconds / 86400);
      const hours = Math.floor((diffSeconds % 86400) / 3600);
      return { text: `${days}d ${hours}h`, isPast: false, diffSeconds };
    }
  };

  // Get the next upcoming event (not past)
  const getNextEvent = () => {
    // First try to find upcoming high-impact events
    const upcomingHighImpact = economicEvents
      .filter(e => e.impact === 'high' && getCountdown(e.date).diffSeconds > -3600)
      .sort((a, b) => new Date(a.date) - new Date(b.date));
    
    if (upcomingHighImpact.length > 0) {
      return upcomingHighImpact[0];
    }
    
    // Then try medium impact
    const upcomingMedium = economicEvents
      .filter(e => e.impact === 'medium' && getCountdown(e.date).diffSeconds > -3600)
      .sort((a, b) => new Date(a.date) - new Date(b.date));
    
    if (upcomingMedium.length > 0) {
      return upcomingMedium[0];
    }
    
    // Fall back to any upcoming event
    const anyUpcoming = economicEvents
      .filter(e => getCountdown(e.date).diffSeconds > -3600)
      .sort((a, b) => new Date(a.date) - new Date(b.date));
    
    return anyUpcoming.length > 0 ? anyUpcoming[0] : null;
  };

  // Fetch economic calendar on mount
  useEffect(() => {
    const fetchCalendar = async () => {
      try {
        const response = await fetch(`${API}/economic-calendar`);
        const data = await response.json();
        
        if (data.success && data.events && data.events.length > 0) {
          // Store all events, not just high impact
          setEconomicEvents(data.events);
          setApiError(false);
        } else {
          setApiError(true);
        }
      } catch (error) {
        console.error('Error fetching economic calendar:', error);
        setApiError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchCalendar();
    // Refresh every hour
    const interval = setInterval(fetchCalendar, 60 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // Get the current next event (recalculated every render due to currentTime change)
  const nextEvent = getNextEvent();
  
  // Get upcoming events for the expanded list (high and medium impact, sorted by time)
  const upcomingEvents = economicEvents
    .filter(e => (e.impact === 'high' || e.impact === 'medium') && getCountdown(e.date).diffSeconds > -3600)
    .sort((a, b) => new Date(a.date) - new Date(b.date))
    .slice(0, 10);

  // Get active sessions
  const getActiveSessions = () => {
    const hour = currentTime.getHours();
    return SESSIONS.filter(s => hour >= s.start && hour < s.end);
  };

  // Calculate session progress position (0-100%)
  const getCurrentPosition = () => {
    const hour = currentTime.getHours();
    const minute = currentTime.getMinutes();
    return ((hour * 60 + minute) / (24 * 60)) * 100;
  };

  const activeSessions = getActiveSessions();
  const currentPosition = getCurrentPosition();

  // Format time
  const formatTime = (date) => {
    return date.toLocaleTimeString('de-DE', { 
      hour: '2-digit', 
      minute: '2-digit',
      second: '2-digit'
    });
  };

  // Format date compact
  const formatDateCompact = (date) => {
    return date.toLocaleDateString('de-DE', {
      weekday: 'short',
      day: '2-digit',
      month: '2-digit'
    });
  };

  const upcomingCount = upcomingEvents.length;

  return (
    <div className="mb-4">
      {/* Compact widget */}
      <div className="px-4 py-3 bg-zinc-900/60 border border-zinc-800/50 rounded-lg">
        {/* Top row: Time, Sessions, News */}
        <div className="flex items-center justify-between gap-4">
          {/* Left: Time & Date */}
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-zinc-500" />
              <span className="font-mono text-lg font-semibold text-white tracking-tight">
                {formatTime(currentTime)}
              </span>
              <span className="text-sm text-zinc-500">
                {formatDateCompact(currentTime)}
              </span>
            </div>
            
            {/* Active Sessions */}
            <div className="flex items-center gap-1.5">
              {activeSessions.length > 0 ? (
                activeSessions.map(session => (
                  <span 
                    key={session.name}
                    className={`px-2 py-0.5 rounded text-xs font-medium ${session.color} text-white`}
                  >
                    {session.name}
                  </span>
                ))
              ) : (
                <span className="text-xs text-zinc-500">Keine aktive Session</span>
              )}
            </div>
          </div>

          {/* Right: News Events */}
          <div className="flex items-center gap-3">
            {loading ? (
              <span className="text-xs text-zinc-500 flex items-center gap-1.5">
                <Newspaper className="h-3.5 w-3.5" />
                News werden geladen...
              </span>
            ) : apiError ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="text-xs text-zinc-500 flex items-center gap-1.5 cursor-help">
                    <Newspaper className="h-3.5 w-3.5" />
                    News nicht verfügbar
                  </span>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  <p className="text-xs">Wirtschaftskalender derzeit nicht erreichbar</p>
                </TooltipContent>
              </Tooltip>
            ) : nextEvent ? (
              <>
                <button 
                  onClick={() => setShowEvents(!showEvents)}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-zinc-800/80 hover:bg-zinc-800 transition-colors"
                >
                  <Newspaper className="h-3.5 w-3.5 text-emerald-400" />
                  <span className="text-xs text-zinc-300 max-w-[140px] truncate">
                    {COUNTRY_FLAGS[nextEvent.country] || '🌍'} {nextEvent.title}
                  </span>
                  <span className={`text-xs font-mono font-medium ${IMPACT_COLORS[nextEvent.impact]?.text || 'text-zinc-400'}`}>
                    {getCountdown(nextEvent.date).text}
                  </span>
                  {upcomingCount > 1 && (
                    <span className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-orange-500/20 text-orange-400 text-xs">
                      +{upcomingCount - 1}
                    </span>
                  )}
                  <ChevronDown className={`h-3 w-3 text-zinc-500 transition-transform ${showEvents ? 'rotate-180' : ''}`} />
                </button>
              </>
            ) : (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="text-xs text-zinc-500 flex items-center gap-1.5 cursor-help">
                    <Newspaper className="h-3.5 w-3.5" />
                    Keine wichtigen News heute
                  </span>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  <p className="text-xs">Keine High-Impact Events für heute geplant</p>
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        </div>

        {/* Session Progress Bar */}
        <div className="mt-3 relative">
          <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden relative">
            {/* Session backgrounds */}
            {SESSIONS.map(session => (
              <div
                key={session.name}
                className={`absolute top-0 h-full ${session.color} opacity-30`}
                style={{
                  left: `${(session.start / 24) * 100}%`,
                  width: `${((session.end - session.start) / 24) * 100}%`
                }}
              />
            ))}
            {/* Current position indicator */}
            <div 
              className="absolute top-1/2 -translate-y-1/2 w-2 h-2 bg-white rounded-full shadow-lg shadow-white/30 z-10"
              style={{ left: `calc(${currentPosition}% - 4px)` }}
            />
          </div>
          {/* Time labels */}
          <div className="flex justify-between mt-1 text-[10px] text-zinc-600">
            <span>00:00</span>
            <span>06:00</span>
            <span>12:00</span>
            <span>18:00</span>
            <span>24:00</span>
          </div>
        </div>
      </div>

      {/* Expandable Events List */}
      {showEvents && upcomingEvents.length > 0 && (
        <div className="mt-2 p-3 bg-zinc-900/40 border border-zinc-800/50 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-zinc-400">Anstehende Events (High & Medium Impact)</span>
            <span className="text-xs text-zinc-500">{upcomingEvents.length} Events</span>
          </div>
          <div className="grid gap-2 max-h-48 overflow-y-auto">
            {upcomingEvents.map((event, idx) => {
              const countdown = getCountdown(event.date);
              return (
                <div 
                  key={idx} 
                  className={`flex items-center justify-between px-3 py-2 rounded bg-zinc-800/50 text-xs ${countdown.isPast ? 'opacity-50' : ''}`}
                >
                  <div className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${IMPACT_COLORS[event.impact]?.dot || 'bg-zinc-500'}`} />
                    <span>{COUNTRY_FLAGS[event.country] || '🌍'}</span>
                    <span className="text-zinc-300 max-w-[200px] truncate">{event.title}</span>
                  </div>
                  <div className="flex items-center gap-3 text-zinc-500">
                    <span>{event.time} Uhr</span>
                    <span className={`font-mono ${IMPACT_COLORS[event.impact]?.text}`}>
                      {countdown.text}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default TradingDayWidget;
